/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { UserRoles } from '../../constants/user';
import { CurrentUserData } from '../../reducers/current-user';

type PermissionsUser = Pick<CurrentUserData, '_id' | 'permissions' | 'initiativeTree'>;

export class InitiativePermissions {
  static canContributeRoles = [UserRoles.Contributor];
  static canVerifyRoles = [UserRoles.Verifier];
  static canManageRoles = [UserRoles.Owner, UserRoles.Manager];
  static canAccessInsightsAndDownloadsRoles = [
    UserRoles.Owner,
    UserRoles.Manager,
    UserRoles.Contributor,
    UserRoles.Verifier,
    UserRoles.ContributorVerifier,
    UserRoles.Viewer,
  ];

  private static hasPermission(user: PermissionsUser, initiativeId: string, roles: UserRoles[], infiniteRecursionProtection: string[] = []): boolean {
    infiniteRecursionProtection.push(initiativeId);
    if (!user.permissions) {
      return false;
    }
    const initiativePermission = user.permissions.find(p => p.initiativeId === initiativeId);
    if (initiativePermission && roles.some(role => initiativePermission.permissions.includes(role))) {
      // Great, we have explicit permissions, early exit
      return true;
    }

    const initiativeTree = user.initiativeTree;
    if (!initiativeTree) {
      return false;
    }

    const initiative = initiativeTree.find(d => d._id === initiativeId);
    if (!initiative?.parentId) {
      return false;
    }

    if (infiniteRecursionProtection.includes(initiative.parentId)) {
      return false;
    }

    const parent = initiativeTree.find(d => d._id === initiative.parentId);
    if (!parent) {
      return false;
    }

    return this.hasPermission(user, parent._id, roles, infiniteRecursionProtection);
  }

  public static isOwner(user: PermissionsUser, initiativeId: string): boolean {
    return InitiativePermissions.hasPermission(
      user,
      initiativeId,
      [
        UserRoles.Owner
      ]
    );
  }

  public static canManageInitiative(user: PermissionsUser, initiativeId: string): boolean {
    return InitiativePermissions.hasPermission(
      user,
      initiativeId,
      InitiativePermissions.canManageRoles
    );
  }

  public static canManageCustomReports(user: PermissionsUser, initiativeId: string): boolean {
    return InitiativePermissions.hasPermission(
      user,
      initiativeId,
      [
        UserRoles.Owner,
        UserRoles.Manager,
        UserRoles.User,
        UserRoles.Contributor,
        UserRoles.Viewer
      ]
    );
  }

  public static canAccessInsightsAndDownloads(user: PermissionsUser, initiativeId: string): boolean {
    return InitiativePermissions.hasPermission(user, initiativeId, this.canAccessInsightsAndDownloadsRoles);
  }

  public static canAccess(user: PermissionsUser, initiativeId: string): boolean {
    return InitiativePermissions.hasPermission(
      user,
      initiativeId,
      [
        UserRoles.Owner,
        UserRoles.Manager,
        UserRoles.User,
        UserRoles.Contributor,
        UserRoles.Verifier,
        UserRoles.Viewer
      ],
    );
  }

  public static canAccessAllSurveyData(user: PermissionsUser, initiativeId: string) {
    return InitiativePermissions.hasPermission(user, initiativeId, [
      UserRoles.Owner,
      UserRoles.Manager,
      UserRoles.Contributor,
      UserRoles.Verifier,
      UserRoles.Viewer,
    ]);
  }

  public static canContribute(user: PermissionsUser, initiativeId: string): boolean {
    return InitiativePermissions.hasPermission(
      user,
      initiativeId,
      this.canContributeRoles,
    );
  }

  public static canVerify(user: PermissionsUser, initiativeId: string): boolean {
    return InitiativePermissions.hasPermission(
      user,
      initiativeId,
      this.canVerifyRoles,
    );
  }

  public static hasRoleToManage({ permissions }: Pick<CurrentUserData, 'permissions'>) {
    if (!permissions || permissions.length === 0) {
      return false;
    }

    return permissions.some(p => p.permissions.some(role => {
      return InitiativePermissions.canManageRoles.includes(role)
    }))
  }
}
